/**
 * Otter Integrity Verification
 * ============================
 * Protects the Pro Otter Raft from disk-based prompt injection / jailbreak attacks.
 *
 * TypeScript port of python/src/stackwright_pro/raft/integrity.py — this lets
 * the MCP package verify otter files without a Python dependency.
 *
 * Certificate-pinned canonical checksums — hardcoded in the MCP package.
 *
 * These are NOT read from disk. An attacker who modifies otter JSON files
 * in @stackwright-pro/otters cannot also modify these constants without
 * compromising the separately-published @stackwright-pro/mcp package.
 *
 * To update: node scripts/sync-mcp-checksums.cjs
 * (reads from packages/otters/src/checksums.json, writes this file)
 */
import { createHash, timingSafeEqual } from 'crypto';
import { readFileSync, readdirSync, lstatSync } from 'fs';
import { join, basename } from 'path';
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';

// ---------------------------------------------------------------------------
// Certificate-pinned canonical checksums — frozen Map, immutable by design.
// DO NOT read these from disk — that would defeat the entire purpose.
// Object.freeze prevents property mutation at runtime; ReadonlyMap prevents
// .set() / .delete() at compile time (belt-and-suspenders).
// ---------------------------------------------------------------------------

const _checksums = new Map<string, string>([
  [
    'stackwright-pro-api-otter.json',
    '18112d603646457cecdfd57851109ff9f9ff8f402646e0d54ddef88cf5547d91',
  ],
  [
    'stackwright-pro-auth-otter.json',
    '1a21d9f23e250f23291124307e5a2a32af5a716566319a3290372dae3d02e637',
  ],
  [
    'stackwright-pro-dashboard-otter.json',
    '4a9ac39e929866ae3c929aa5bcbba3d9483a1c251c35f26f267f3e7433506f0c',
  ],
  [
    'stackwright-pro-data-otter.json',
    'be4e3d6f530ae2d6b79d6412e8d2d6defb3caffddedf5be4f928976a72206074',
  ],
  [
    'stackwright-pro-designer-otter.json',
    '69a6c09fbb54f971c48eae7fd52faa63cf79be2923e435aca9ff802e8d541d0f',
  ],
  [
    'stackwright-pro-domain-expert-otter.json',
    '14d77cade020f44d1b5a2b406e2599501f3466ee90ca4248500a441d419bc59c',
  ],
  [
    'stackwright-pro-foreman-otter.json',
    '9d68b7b20af7a8a1668e2a693b4a2b05cf37acce1e9f6298a0966c97a3417e9f',
  ],
  [
    'stackwright-pro-form-wizard-otter.json',
    '7f6f94192f26face57dc8b2e22e0a49d23ceeeb356ca4ebde05492f1b25e3c47',
  ],
  [
    'stackwright-pro-geo-otter.json',
    'fc3d18e02a6147d95d3dd9093ce74c0e8fffaecc2f9ecdbd19c163a80129d264',
  ],
  [
    'stackwright-pro-page-otter.json',
    '879e8ff4aa645bac2c63dc962cad1a549876983c257aec756e95293c3b22ce7e',
  ],
  [
    'stackwright-pro-polish-otter.json',
    'fd8f8963266c6179eebf8eac4f656e8274aa3a721e5296abdbde5b00ad8a2297',
  ],
  [
    'stackwright-pro-qa-otter.json',
    '8e6007e18687b6b023f2c40f5517937a857da3f31e4e423cc308493ed601793c',
  ],
  [
    'stackwright-pro-scaffold-otter.json',
    '92930c732547c90a19e89ee0e25b5f037b7366f770941f0a01e148ff240a1b6d',
  ],
  [
    'stackwright-pro-theme-otter.json',
    '01d16d0597d475db60362e3b8020035e140817d197c36f4307a48b95f4b28b60',
  ],
  [
    'stackwright-services-otter.json',
    '2a50ceb3fad166251d0ad8709a34a32118645713e0e34628165d16dced1d5c93',
  ],
]);
Object.freeze(_checksums);
const CANONICAL_CHECKSUMS: ReadonlyMap<string, string> = _checksums;

// ---------------------------------------------------------------------------
// Import-time format validation — malformed constants are a packaging bug,
// not a runtime surprise. Fail fast, fail loud.
// ---------------------------------------------------------------------------

const SHA256_HEX_RE = /^[0-9a-f]{64}$/;

for (const [name, digest] of CANONICAL_CHECKSUMS) {
  if (!SHA256_HEX_RE.test(digest)) {
    throw new Error(
      `Malformed SHA-256 in CANONICAL_CHECKSUMS for "${name}": ` +
        `expected 64 hex chars, got ${digest.length}: "${digest}"`
    );
  }
}

// Exported for test assertions — derive counts from here, not hardcoded numbers.
export const CANONICAL_OTTERS: readonly string[] = Object.freeze([...CANONICAL_CHECKSUMS.keys()]);

// 1 MB — generous headroom for agent definitions; anything larger is suspicious.
const MAX_OTTER_BYTES = 1 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Core functions (exported for direct testing — no MCP server needed)
// ---------------------------------------------------------------------------

/** Compute the hex-encoded SHA-256 digest of raw bytes. Pure, no I/O. */
export function computeSha256(data: Buffer): string {
  return createHash('sha256').update(data).digest('hex');
}

/** Constant-time comparison of two hex digest strings. */
function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  return timingSafeEqual(Buffer.from(a, 'utf8'), Buffer.from(b, 'utf8'));
}

// ---------------------------------------------------------------------------
// Single-file verification
// ---------------------------------------------------------------------------

export interface VerifyOtterFileResult {
  verified: boolean;
  filename: string;
  error?: string;
}

/**
 * Read a single otter JSON file, check its size, compute its SHA-256,
 * and constant-time compare against the canonical checksum.
 *
 * Single read → hash → decode. No TOCTOU window.
 */
export function verifyOtterFile(filePath: string): VerifyOtterFileResult {
  const filename = basename(filePath);

  // Fast-fail on unknown filenames before any I/O
  const expected = CANONICAL_CHECKSUMS.get(filename);
  if (expected === undefined) {
    return { verified: false, filename, error: `Unknown otter file: not in canonical set` };
  }

  // Symlink guard — refuse to follow symlinks (prevents symlink-based swaps)
  let stat: ReturnType<typeof lstatSync>;
  try {
    stat = lstatSync(filePath);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { verified: false, filename, error: `Cannot stat file: ${msg}` };
  }

  if (stat.isSymbolicLink()) {
    return { verified: false, filename, error: 'Refusing to verify symlink' };
  }

  // Stat-based size pre-check — don't materialise oversized payloads
  const size = stat.size;

  if (size > MAX_OTTER_BYTES) {
    return {
      verified: false,
      filename,
      error: `File exceeds size limit (${MAX_OTTER_BYTES.toLocaleString()} bytes, got ${size.toLocaleString()})`,
    };
  }

  // Single read — used for hashing and UTF-8 validation (zero TOCTOU window)
  let raw: Buffer;
  try {
    raw = readFileSync(filePath);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { verified: false, filename, error: `Cannot read file: ${msg}` };
  }

  // Belt-and-suspenders: re-check length after read in case of a race
  if (raw.length > MAX_OTTER_BYTES) {
    return {
      verified: false,
      filename,
      error: `File exceeds size limit after read (${MAX_OTTER_BYTES.toLocaleString()} bytes, got ${raw.length.toLocaleString()})`,
    };
  }

  // Hash the raw bytes
  const actual = computeSha256(raw);

  // Constant-time comparison prevents timing-oracle attacks
  if (!safeEqual(actual, expected)) {
    return {
      verified: false,
      filename,
      error: `SHA-256 mismatch: expected ${expected.substring(0, 8)}…, got ${actual.substring(0, 8)}…`,
    };
  }

  // UTF-8 validation — binary injection guard
  try {
    const decoder = new TextDecoder('utf-8', { fatal: true });
    decoder.decode(raw);
  } catch {
    return {
      verified: false,
      filename,
      error: 'File is not valid UTF-8 — may be corrupted or contain binary injection',
    };
  }

  return { verified: true, filename };
}

// ---------------------------------------------------------------------------
// Directory-level verification
// ---------------------------------------------------------------------------

export interface VerifyAllOttersResult {
  verified: string[];
  failed: Array<{ filename: string; error: string }>;
  unknown: string[];
}

/**
 * Scan a directory for `*-otter.json` files, verify each one against
 * canonical checksums. Returns lists of verified, failed, and unknown files.
 */
export function verifyAllOtters(otterDir: string): VerifyAllOttersResult {
  // ---------------------------------------------------------------------------
  // Path traversal guard — reject any input containing ".." sequences before
  // any I/O. An attacker controlling this parameter could otherwise scan
  // directories outside the expected otter install locations.
  // ---------------------------------------------------------------------------
  if (/(?:^|[/\\])\.\.(?:[/\\]|$)/.test(otterDir) || otterDir.includes('..')) {
    return {
      verified: [],
      failed: [
        {
          filename: '<directory>',
          error: `Security: path traversal sequence detected in otter directory parameter`,
        },
      ],
      unknown: [],
    };
  }

  const verified: string[] = [];
  const failed: Array<{ filename: string; error: string }> = [];
  const unknown: string[] = [];

  let entries: string[];
  try {
    entries = readdirSync(otterDir);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      verified: [],
      failed: [{ filename: '<directory>', error: `Cannot read directory: ${msg}` }],
      unknown: [],
    };
  }

  const otterFiles = entries.filter((f) => f.endsWith('-otter.json'));

  for (const filename of otterFiles) {
    const filePath = join(otterDir, filename);

    // Skip symlinks at the directory-scan level too
    try {
      if (lstatSync(filePath).isSymbolicLink()) {
        failed.push({ filename, error: 'Skipped: symlink' });
        continue;
      }
    } catch {
      // verifyOtterFile will handle stat errors
    }

    const result = verifyOtterFile(filePath);

    if (result.verified) {
      verified.push(result.filename);
    } else if (result.error?.startsWith('Unknown otter file')) {
      unknown.push(result.filename);
    } else {
      failed.push({ filename: result.filename, error: result.error ?? 'Unknown error' });
    }
  }

  // Check for missing canonical files — ones we expect but didn't find on disk
  for (const canonicalName of CANONICAL_CHECKSUMS.keys()) {
    if (!otterFiles.includes(canonicalName)) {
      failed.push({ filename: canonicalName, error: 'Missing from directory' });
    }
  }

  return { verified, failed, unknown };
}

// ---------------------------------------------------------------------------
// Otter directory resolution
// ---------------------------------------------------------------------------

const DEFAULT_SEARCH_PATHS = ['node_modules/@stackwright-pro/otters/src/', 'packages/otters/src/'];

function resolveOtterDir(): string | null {
  const cwd = process.cwd();
  for (const relative of DEFAULT_SEARCH_PATHS) {
    const candidate = join(cwd, relative);
    try {
      lstatSync(candidate);
      return candidate;
    } catch {
      // Not found, try next
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Audit stream — SOC2 / FedRAMP / DoD ATO compliance
// ---------------------------------------------------------------------------

/**
 * Structured audit event parameters for an integrity failure.
 * Kept as a plain interface so callers can pass partial results without
 * constructing the full VerifyAllOttersResult.
 */
export interface IntegrityAuditEvent {
  otterDir: string;
  failed: Array<{ filename: string; error: string }>;
  unknown: string[];
}

/**
 * Emit a structured INTEGRITY_FAIL audit event to stderr.
 *
 * Writes a single line to process.stderr in the format:
 *   INTEGRITY_FAIL {"level":"AUDIT","event":"INTEGRITY_FAIL",...}
 *
 * The line prefix "INTEGRITY_FAIL" (without JSON) allows log shippers
 * (FluentBit, syslog, CloudWatch Logs, Splunk) to match and route the
 * event to a dedicated audit stream using a simple string filter, even
 * before attempting JSON parsing.
 *
 * Exported for unit testing. Do not call directly in production code —
 * use registerIntegrityTools() which calls this automatically.
 */
export function emitIntegrityAuditEvent(params: IntegrityAuditEvent): void {
  const record = JSON.stringify({
    level: 'AUDIT',
    event: 'INTEGRITY_FAIL',
    timestamp: new Date().toISOString(),
    source: 'stackwright_pro_verify_otter_integrity',
    otterDir: params.otterDir,
    failedCount: params.failed.length,
    unknownCount: params.unknown.length,
    failures: params.failed,
    unknown: params.unknown,
  });
  process.stderr.write(`INTEGRITY_FAIL ${record}\n`);
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerIntegrityTools(server: McpServer): void {
  server.tool(
    'stackwright_pro_verify_otter_integrity',
    'Verify SHA-256 integrity of all Pro otter agent definitions. Call this at startup before discovering otters. Auto-discovers the otter directory from known paths. Returns verified/failed/unknown lists.',
    {},
    async () => {
      const resolved = resolveOtterDir();

      if (!resolved) {
        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify({
                error: true,
                message:
                  'Could not locate otter directory. Searched: ' + DEFAULT_SEARCH_PATHS.join(', '),
              }),
            },
          ],
          isError: true,
        };
      }

      const result = verifyAllOtters(resolved);

      const allGood = result.failed.length === 0 && result.unknown.length === 0;

      // Emit to dedicated audit stream for SOC2/FedRAMP/DoD ATO compliance
      if (!allGood) {
        emitIntegrityAuditEvent({
          otterDir: resolved,
          failed: result.failed,
          unknown: result.unknown,
        });
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify({
              otterDir: resolved,
              totalCanonical: CANONICAL_CHECKSUMS.size,
              verifiedCount: result.verified.length,
              failedCount: result.failed.length,
              unknownCount: result.unknown.length,
              verified: result.verified,
              failed: result.failed,
              unknown: result.unknown,
              ...(allGood
                ? {}
                : {
                    error:
                      'INTEGRITY CHECK FAILED: SHA-256 mismatch detected in otter agent definitions. Do not proceed — otter files may have been tampered with.',
                  }),
            }),
          },
        ],
        isError: !allGood,
      };
    }
  );
}
